import { useState } from "react";
import { Outlet } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useRecordPageVisits } from "@/features/page-visits/page-visits";
import { AppFooter } from "./app-footer";
import { AppHeader } from "./app-header";
import { AppSidebar } from "./app-sidebar";
import { ImpersonationBanner } from "@/features/auth/impersonation-banner";
import { NewBusinessDialog } from "@/features/business/new-business-dialog";
import { CommandPalette } from "./command-palette";
import { useNavActions } from "./nav-actions.store";

/**
 * The signed-in frame: sidebar, header, scrolling content, footer.
 *
 * Only the content column scrolls — the sidebar and header stay put, so on a
 * long report the navigation is still one click away instead of a scroll to top.
 */
export function AppShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  // The sidebar's two non-navigating entries open their dialog from here, so
  // the button that triggers them does not have to reach the shell by props.
  const action = useNavActions((state) => state.active);
  const closeAction = useNavActions((state) => state.close);
  // Every signed-in page passes through here, so this is the one place a
  // visit can be counted for the home page's "frequently visited" list.
  useRecordPageVisits();

  return (
    <TooltipProvider>
      {/* Above the frame, not inside the scrolling column: while impersonating
          this is the only way back, so it must never scroll out of reach. */}
      <div className="bg-background flex h-svh flex-col overflow-hidden">
        <ImpersonationBanner />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <AppSidebar onOpenSearch={() => setSearchOpen(true)} />

          <div className="flex min-w-0 flex-1 flex-col">
            <AppHeader onOpenSearch={() => setSearchOpen(true)} />

            <main className="flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-[100rem] px-4 py-6 sm:px-6 lg:px-8">
                <Outlet />
              </div>
              <AppFooter />
            </main>
          </div>
        </div>
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />

      <NewBusinessDialog
        open={action === "newBusiness"}
        onOpenChange={(next) => {
          if (!next) closeAction();
        }}
      />
    </TooltipProvider>
  );
}
